import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { catchError, map } from "rxjs/operators";
import { formatDate } from '@angular/common';
import { IInvoiceDetails } from '../historical-data/historical-invoice-detail/historical-invoice-detail.component';
import { ListPaymentMethodsResponse } from '../models/payment/payment-methods/ListPaymentMethodsResponse';

export interface IMetadata {
  id: string;
  uri: string;
  type: string;
}

export interface IBanData {
  metadata: IMetadata;
  parent: string;
  child: string;
  bancust: string;
  contractacc: string;
  compcode: string;
  zzachclass_bus: string;
  vktyp: string;
}

@Injectable({
  providedIn: 'root',
})
export class InvoicesService {
  constructor(private http: HttpClient) { }

  async getBans(customerIDs: string[]): Promise<Observable<IBanData[]>> {
    const url = `${environment.serverURL}${environment.brimURI}bans?customerIDs=${customerIDs.join(',')}`;
    console.log(url)

    let logTraceJSON: any = { 'eventSource': 'getBansByCustomerId', 'customerIDs': `${customerIDs.join(',')}` };
    const headers = new HttpHeaders({
      'X-Log-Trace': JSON.stringify(logTraceJSON),
    });
    return this.http.get<IBanData[]>(url, { headers }).pipe(
      map((response) => {
        if (response.length === 0) {
          throw new Error('No bans found');
        }
        const filterRes = response.filter((res: any) => res.bancust !== '');
        return filterRes;
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  async getBansFromBQ(bans: Array<string>): Promise<Observable<IBanData[]>> {
    const url = `${environment.serverURL}${environment.pgURI}big-query/ban/metadata`;
    let logTraceJSON: any = { 'eventSource': 'getBansFromBQ', 'bans': bans?.join?.(', ') };
    const headers = new HttpHeaders({
      'X-Log-Trace': JSON.stringify(logTraceJSON),
    });
    return this.http.post<IBanData[]>(url, { bans }, { headers }).pipe(
      map((response) => {
        if (response.length === 0) {
          throw new Error('No bans found');
        }
        const filterRes = response.filter((res: any) => res.bancust !== '' && res.vktyp != null)
        .map(res => {
          if(res.bancust.startsWith('0') && res.bancust.length === 10) res.bancust = res.bancust.substring(1)
          return res
        });
        return filterRes;
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  async getBANsMetadata(customerIds: string[], bans: string[]): Promise<Observable<IBanData[]>> {
    const url = `${environment.serverURL}${environment.brimURI}bans/metadata`;
    let logTraceJSON: any = { 'eventSource': 'getBansMetadata', 'customerIDs': `${customerIds.join(',')}`, 'BANs': `${bans.join(',')}` };
    const headers = new HttpHeaders({
      'X-Log-Trace': JSON.stringify(logTraceJSON),
    });
    return this.http.post<IBanData[]>(url, { customerIds, bans }, { headers }).pipe(
      map((response) => {
        return response.filter((res: any) => res.bancust !== '' || (res.vktype !== null && res.vktype !== undefined && res.vktype !== '')).map?.(res => {
          if (res.bancust.startsWith('0') && res.bancust.length === 10) res.bancust = res.bancust.substring(1)
          return res
        });
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  public getHistoricalInvoiceHeader(ban: string, caNo: any): Observable<any> {
    let url;
    if (environment.historicalBan) {
      url = environment.serverURL + environment.pgURI + 'big-query/invoice/header/' + environment.historicalBan;
    }
    else {
      url = environment.serverURL + environment.pgURI + 'big-query/invoice/header/' + ban;
    }
    let logTraceJSON: any = { 'eventSource': 'getHistoricalInvoiceList', 'ban': environment.historicalBan ? environment.historicalBan : ban, 'contractAccount': caNo };
    const headers = new HttpHeaders({
      'X-Log-Trace': JSON.stringify(logTraceJSON),
    });
    return this.http.get(url, { headers });
  }

  public getTotalInvoices(customerIDs: string): Observable<any> {
    const url = `${environment.serverURL}${environment.brimURI}billing/widget`;
    console.log('url', url);

    let params = new HttpParams().set('customerIDs', customerIDs);
    let logTraceJSON: any = { 'eventSource': 'getBillingWidgets', 'customerIDs': `${customerIDs}` };
    const headers = new HttpHeaders({
      'X-Log-Trace': JSON.stringify(logTraceJSON),
    });
    return this.http.get(url, { headers, params });
  }

  public getHistoricalInvoiceDetail(bpNo: string, invoiceId: string): Observable<IInvoiceDetails[]> {
    const url = environment.serverURL + environment.pgURI + 'big-query/invoice/' + bpNo + '/' + invoiceId;
    let logTraceJSON: any = { 'eventSource': 'getHistoricalInvoiceDetail', 'ban': bpNo, 'invoiceNumber': invoiceId }
    const headers = new HttpHeaders({
      'X-Log-Trace': JSON.stringify(logTraceJSON),
    });
    return this.http.get<IInvoiceDetails[]>(url, { headers });
  }

  public getInvoices(bpNo: string, caNo: string, cNo: string, isTSA: boolean): Observable<any> {
    const url = environment.serverURL + environment.brimURI + 'invoices';

    let queryParams = new HttpParams()
      .append('bpNo', bpNo)
      .append('caNo', caNo)
      .append('cNo', cNo)
      .append('tsa', isTSA);

    let logTraceJSON: any = { 'eventSource': 'getInvoiceList', 'ban': bpNo, 'contractAccount': caNo };
    const headers = new HttpHeaders({
      'X-Log-Trace': JSON.stringify(logTraceJSON),
    });
    return this.http.get(url, { headers, params: queryParams });
  }

  public downloadInvoice(id: string): Observable<any> {
    const url = environment.serverURL + environment.brimURI + 'invoice/download';
    let queryParams = new HttpParams()
      .append('id', id);

    let logTraceJSON: any = { 'eventSource': 'downloadInvoiceById', 'invoiceNumber': id };
    const headers = new HttpHeaders({
      'X-Log-Trace': JSON.stringify(logTraceJSON),
    });

    return this.http.get(url, { headers, params: queryParams, responseType: 'arraybuffer' }).pipe(map((response) => {
      return {
        filename: atob(id) + '.pdf',
        data: response,
      };
    }));
  }

  getInvoiceDetail(invoiceId: any, bpNo: any, caNo: any, count: boolean, top: any, skip: any, isTSA = false) {
    let queryParams = new HttpParams()
      .append('bpNo', bpNo)
      .append('caNo', caNo)
      .append('cNo', '')
      .append('count', count)
      .append('top', top)
      .append('skip', skip)
      .append('tsa', isTSA);

    const url = environment.serverURL + environment.brimURI + 'invoicesDetails/' + invoiceId;

    let logTraceJSON: any = { 'eventSource': 'getInvoiceDetails', 'ban': bpNo, 'contractAccount': caNo, 'invoiceNumber': invoiceId };
    const headers = new HttpHeaders({
      'X-Log-Trace': JSON.stringify(logTraceJSON),
    });

    return this.http.get(url, { headers, params: queryParams });
  }

  getPaymentHistory(bpNo: any, caNo: any, isTSA: boolean) {
    let queryParams = new HttpParams()
      .append('bpNo', bpNo)
      .append('caNo', caNo)
      .append('tsa', isTSA);

    const url = environment.serverURL + environment.brimURI + 'paymentHistory';
    let logTraceJSON: any = { 'eventSource': 'getPaymentHistoryOld', 'ban': bpNo, 'contractAccount': caNo };
    const headers = new HttpHeaders({
      'X-Log-Trace': JSON.stringify(logTraceJSON),
    });
    return this.http.get(url, { headers, params: queryParams });
  }

  getPaymentHistoryNew(bpNo: any, caNo: any) {
    let queryParams = new HttpParams()
      .append('bpNo', bpNo)

    const url = environment.serverURL + environment.brimURI + 'paymentHistoryNeo';
    let logTraceJSON: any = { 'eventSource': 'getPaymentHistoryNew', 'ban': bpNo, 'contractAccount': caNo };
    const headers = new HttpHeaders({
      'X-Log-Trace': JSON.stringify(logTraceJSON),
    });
    return this.http.get(url, { headers, params: queryParams });
  }

  getHistoricalPaymentHistory(bpNo: any, caNo: any) {
    let url;
    if (environment.historicalBan) {
      url = environment.serverURL + environment.pgURI + 'big-query/payment/' + environment.historicalBan;
    }
    else {
      url = environment.serverURL + environment.pgURI + 'big-query/payment/' + bpNo;
    }
    let logTraceJSON: any = { 'eventSource': 'getHistoricalPaymentHistoryBQ', 'ban': environment.historicalBan ? environment.historicalBan : bpNo, 'contractAccount': caNo };
    const headers = new HttpHeaders({
      'X-Log-Trace': JSON.stringify(logTraceJSON),
    });
    return this.http.get(url, { headers });
  }

  async getUsageRecord(bpNo: any, fromDate: any, toDate: any) {
    let queryParams = new HttpParams()
      .append('bpNo', bpNo)
      .append('fromDate', fromDate)
      .append('toDate', toDate)

    const url = environment.serverURL + environment.brimURI + 'usage-records';
    let logTraceJSON: any = { 'eventSource': 'getUsageRecordsBRIM', 'ban': bpNo };
    const headers = new HttpHeaders({
      'X-Log-Trace': JSON.stringify(logTraceJSON),
    });
    return await this.http.get(url, { headers, params: queryParams });
  }

  async getBQUsageRecord(bpNo: any, fromDate: any, toDate: any) {
    const url = environment.serverURL + environment.pgURI + `big-query/usage/${bpNo}/${fromDate}/${toDate}`;
    let logTraceJSON: any = { 'eventSource': 'getUsageRecordsBQ', 'ban': bpNo };
    const headers = new HttpHeaders({
      'X-Log-Trace': JSON.stringify(logTraceJSON),
    });
    return await this.http.get(url, { headers });
  }

  deleteRecurring(data: any) {
    const url = environment.serverURL + environment.pgURI + 'payment/schedule';
    let logTraceJSON: any = { 'eventSource': 'deleteRecurringSchedule', 'payload': data };
    const headers = new HttpHeaders({
      'X-Log-Trace': JSON.stringify(logTraceJSON),
    });
    return this.http.delete(url, { headers, body: data });
  }


  async getRecurringSchedule(ban: any) {
    const url = environment.serverURL + environment.pgURI + 'payment/schedule/' + ban;
    let logTraceJSON: any = { 'eventSource': 'getRecurringSchedule', 'ban': ban };
    const headers = new HttpHeaders({
      'X-Log-Trace': JSON.stringify(logTraceJSON),
    });
    return await this.http.get(url, { headers });
  }

  getPaymentMethods(ban: any) {
    const url = environment.serverURL + environment.pgURI + 'payment/list-payment-methods/' + ban;
    let logTraceJSON: any = { 'eventSource': 'getPaymentMethodList', 'ban': ban };
    const headers = new HttpHeaders({
      'X-Log-Trace': JSON.stringify(logTraceJSON),
    });
    return this.http.get<ListPaymentMethodsResponse>(url, { headers });
  }

  getPaperlessBilling(ban: string) {
    const url = `${environment.serverURL}${environment.pgURI}customer/enw/details?bans=${ban}`;
    let logTraceJSON: any = { 'eventSource': 'getPaperlessBillingData', 'ban': ban };
    const headers = new HttpHeaders({
      'X-Log-Trace': JSON.stringify(logTraceJSON),
    });
    return this.http.get(url, { headers });
  }

  UpdatePaperlessIndicator(ban: string, paperlessIndicator: string): Observable<any> {
    const url = `${environment.serverURL}${environment.pgURI}customer/enw/details/${ban}`;
    const body = {
      paperlessIndicator: paperlessIndicator
    };
    let logTraceJSON: any = { 'eventSource': 'deleteRecurringSchedule', 'ban': ban, 'payload': body };
    const headers = new HttpHeaders({
      'X-Log-Trace': JSON.stringify(logTraceJSON),
    });
    return this.http.put(url, body, { headers });
  }
}